import { useEffect, useMemo, useState } from 'react';
import { Search, Home, Users, FileText, LayoutDashboard, CreditCard, User, Shield, AlertTriangle, BarChart3, Headphones } from 'lucide-react';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import api from '@/services/api';
import { navigate } from '@/services/navigation';

const ITEM = 'data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 cursor-pointer';

const PAGES = [
  { label: 'Home', href: '/landlord/dashboard', icon: LayoutDashboard },
  { label: 'Properties', href: '/landlord/properties', icon: Home },
  { label: 'Tenants', href: '/landlord/tenants', icon: Users },
  { label: 'Reports', href: '/landlord/reports', icon: FileText },
  { label: 'Disputes', href: '/landlord/disputes', icon: AlertTriangle },
  { label: 'Analytics', href: '/landlord/analytics', icon: BarChart3 },
  { label: 'Team', href: '/landlord/team', icon: Users },
  { label: 'Support', href: '/landlord/support', icon: Headphones },
  { label: 'Billing', href: '/landlord/billing', icon: CreditCard },
  { label: 'Profile', href: '/landlord/profile', icon: User },
  { label: 'Security', href: '/landlord/security', icon: Shield },
];

export const CommandPalette = ({ open, onOpenChange, enabled = true }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open || data || !enabled) return;
    Promise.all([api.get('/properties?limit=500'), api.get('/tenants')])
      .then(([p, t]) => setData({ properties: p.data.items || [], tenants: t.data.items || [] }))
      .catch(() => setData({ properties: [], tenants: [] }));
  }, [open, data, enabled]);

  const go = (href) => { onOpenChange(false); navigate(href); };

  const tenants = useMemo(() => (data?.tenants || []).slice(0, 200), [data]);
  const properties = useMemo(() => (data?.properties || []).slice(0, 200), [data]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search tenants, properties, pages…" data-testid="command-palette-input" />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>{data || !enabled ? 'No results.' : 'Loading…'}</CommandEmpty>
        <CommandGroup heading="Pages">
          {PAGES.map((p) => (
            <CommandItem key={p.href} value={`page ${p.label}`} className={ITEM} onSelect={() => go(p.href)} data-testid={`cmd-page-${p.label.toLowerCase()}`}>
              <p.icon className="w-4 h-4 mr-2 text-slate-400" />{p.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {tenants.length > 0 && (
          <CommandGroup heading="Tenants">
            {tenants.map((t) => (
              <CommandItem key={t.id} value={`tenant ${t.full_name} ${t.email || ''} ${t.tenant_unique_id || ''} ${t.id}`} className={ITEM} onSelect={() => go(`/landlord/tenants?tenant=${t.id}`)}>
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <span className="flex-1 truncate">{t.full_name}</span>
                <span className="font-mono text-[11px] text-slate-400 ml-2">{t.tenant_unique_id}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {properties.length > 0 && (
          <CommandGroup heading="Properties">
            {properties.map((p) => (
              <CommandItem key={p.id} value={`property ${p.address?.street} ${p.address?.unit_number || ''} ${p.address?.city} ${p.address?.postal_code || ''} ${p.id}`} className={ITEM} onSelect={() => go(p.verification_status === 'verified' ? '/landlord/properties' : `/landlord/properties/${p.id}/verify`)}>
                <Home className="w-4 h-4 mr-2 text-slate-400" />
                <span className="flex-1 truncate">{p.address?.street}{p.address?.unit_number ? ` #${p.address.unit_number}` : ''}</span>
                <span className="text-[11px] text-slate-400 ml-2">{p.address?.city}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export const CommandTrigger = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="hidden sm:inline-flex items-center gap-2 h-8 px-2.5 rounded-md border border-slate-200 bg-white/70 hover:bg-white text-slate-500 text-xs transition-colors"
    data-testid="command-palette-trigger"
  >
    <Search className="w-3.5 h-3.5" />
    <span>Search</span>
    <kbd className="ml-1 font-mono text-[10px] text-slate-400 border border-slate-200 rounded px-1 leading-4">⌘K</kbd>
  </button>
);
